/**
 * The order of people inside a voice channel (owner decision 4, 2026-10-28,
 * the way TeamSpeak sorts by server group): the highest role first, as the
 * roles are ordered in Server settings (Discord's order: position, then the
 * older role on a tie), then by display name, compared for the UI's
 * language (English; a name in any script), then by user ID so the order never jumps.
 *
 * The sidebar rows, the voice room tiles and anything else that lists a
 * channel's people use this one function (`voiceStatesIn`), so they agree.
 * The owner is sorted by their roles like everyone else: ownership is not a
 * role.
 */
import {
  compareSnowflakes,
  roleRank,
  type Guild,
  type GuildMember,
  type Snowflake,
  type User,
  type VoiceState,
} from '@neosocial/protocol';

const collators = new Map<string, Intl.Collator>();

/**
 * A cached collator for names, numbers in numeric order. The UI is English
 * (`lang` is always `en`); names in any script sort by the Unicode
 * collation's own order (Latin, then Arabic script, each alphabetically).
 */
export function nameCollator(_lang?: string): Intl.Collator {
  const key = 'en';
  let c = collators.get(key);
  if (!c) {
    c = new Intl.Collator(key, { sensitivity: 'base', numeric: true });
    collators.set(key, c);
  }
  return c;
}

/** The best rank among a member's roles (0 with none), the owner included. */
export function roleOrderRank(guild: Guild | undefined, roleIds: readonly Snowflake[]): number {
  if (!guild) return 0;
  let best = 0;
  for (const id of roleIds) {
    const r = guild.roles.find((x) => x.id === id);
    if (r) best = Math.max(best, roleRank(guild.roles, r, guild.id));
  }
  return best;
}

export interface VoiceOrderInput {
  guild: Guild | undefined;
  members: Record<Snowflake, GuildMember> | undefined;
  users: Record<Snowflake, User>;
  lang: string;
}

function shownName(id: Snowflake, i: VoiceOrderInput): string {
  const m = i.members?.[id];
  const u = i.users[id] ?? m?.user;
  return m?.nick || u?.global_name || u?.username || id;
}

/** Sorts anything that names a user: highest role first, then name, then ID. Returns a new array. */
export function sortByVoiceOrder<T>(
  items: readonly T[],
  userIdOf: (x: T) => Snowflake,
  i: VoiceOrderInput,
): T[] {
  const coll = nameCollator(i.lang);
  return items
    .map((x) => {
      const id = userIdOf(x);
      return {
        x,
        id,
        rank: roleOrderRank(i.guild, i.members?.[id]?.roles ?? []),
        name: shownName(id, i),
      };
    })
    .sort(
      (a, b) => b.rank - a.rank || coll.compare(a.name, b.name) || compareSnowflakes(a.id, b.id),
    )
    .map((e) => e.x);
}

/** Sorts a channel's voice states (see `sortByVoiceOrder`). */
export function sortVoiceStates(states: readonly VoiceState[], i: VoiceOrderInput): VoiceState[] {
  return sortByVoiceOrder(states, (v) => v.user_id, i);
}
